// Divako brand palette — copied from the mobile app's lib/colors.ts
// (/Users/geof/Divako/GitLab/portal-app/lib/colors.ts), which itself mirrors
// the Bootstrap `:root` custom properties of the legacy web portal. Keep the
// two files in sync.
//
// The semantic tokens (water, danger, success, …) match the `m_meter.color`
// values the platform stores, which is what makes `meterBrand()` below a
// direct name → color lookup.

export const Bs = {
  blue: '#0d6efd',
  indigo: '#6610f2',
  purple: '#6f42c1',
  pink: '#d63384',
  red: '#dc3545',
  orange: '#fd7e14',
  yellow: '#fdd757',
  green: '#198754',
  teal: '#20c997',
  cyan: '#0dcaf0',
  black: '#000000',
  white: '#ffffff',
  gray: '#6c757d',
  grayDark: '#343a40',
  gray100: '#f8f9fa',
  gray200: '#e9ecef',
  gray300: '#dee2e6',
  gray400: '#ced4da',
  gray500: '#adb5bd',
  gray600: '#6c757d',
  gray700: '#495057',
  gray800: '#343a40',
  gray900: '#212529',
  semiLight: '#EDEFF2',

  // Semantic brand tokens — these names match m_meter.color values.
  primary: '#001E30',
  main: '#06426a',
  water: '#3B7DDD',
  info: '#9cbeac',
  dark: '#212529',
  light: '#f8f9fa',
  danger: '#E84F3D',
  warning: '#F5A652',
  success: '#3AAC70',
  secondary: '#6e7785',

  // Soft "-light" variants (soft fills, secondary chart series).
  primaryLight: '#b0ceee',
  mainLight: '#b0ceee',
  waterLight: '#d3e2f7',
  blueLight: '#c0d2ff',
  secondaryLight: '#caced1',
  successLight: '#c5efce',
  infoLight: '#c7d7cf',
  warningLight: '#f4cd9c',
  dangerLight: '#f6cdd1',
  purpleLight: '#9984c0',
  darkLight: '#bebfc1',
} as const;

// ---- small color math (so we can derive legible shades from one hue) ----

export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace('#', '');
  const full =
    h.length === 3
      ? h
          .split('')
          .map((c) => c + c)
          .join('')
      : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

// rgbString returns "r, g, b" for use inside rgba(...) strings.
export function rgbString(hex: string): string {
  const { r, g, b } = hexToRgb(hex);
  return `${r}, ${g}, ${b}`;
}

function toHexByte(n: number): string {
  return Math.max(0, Math.min(255, Math.round(n)))
    .toString(16)
    .padStart(2, '0');
}

// mix blends color `a` toward color `b` by `t` (0 = all a, 1 = all b).
export function mix(a: string, b: string, t: number): string {
  const ca = hexToRgb(a);
  const cb = hexToRgb(b);
  return (
    '#' +
    toHexByte(ca.r + (cb.r - ca.r) * t) +
    toHexByte(ca.g + (cb.g - ca.g) * t) +
    toHexByte(ca.b + (cb.b - ca.b) * t)
  );
}

// ---- meter color → brand palette ----

export type MeterBrand = {
  // The exact brand color. Used as the icon on light surfaces and as the
  // seed for the card gradient.
  solid: string;
  // The official "-light" variant — soft fills / secondary series.
  soft: string;
  // A lightened brand tint that stays legible as text/icon on the dark
  // card surfaces (dark mode).
  onDark: string;
  // A deeply darkened, hue-tinted surface for dark-mode cards.
  base: string;
  // Companion color for a comparison series ("last year") drawn next to
  // `solid`. A genuinely different hue — not a faded tint of the brand
  // color — so the two series stay tellable-apart: warm orange against the
  // cool/neutral brands, brand water blue against the warm/green ones
  // (blue↔orange is also the colorblind-safe pairing).
  compare: string;
  // `compare` lifted for dark card surfaces, same treatment as `onDark`.
  compareOnDark: string;
  // "r, g, b" of `solid`, for rgba() gradients.
  rgbString: string;
};

// Base brand color + its soft variant for each m_meter.color value.
const METER_BRAND_BASE: Record<string, { color: string; light: string }> = {
  water: { color: Bs.water, light: Bs.waterLight },
  primary: { color: Bs.primary, light: Bs.primaryLight },
  main: { color: Bs.main, light: Bs.mainLight },
  danger: { color: Bs.danger, light: Bs.dangerLight },
  warning: { color: Bs.warning, light: Bs.warningLight },
  success: { color: Bs.success, light: Bs.successLight },
  info: { color: Bs.info, light: Bs.infoLight },
  secondary: { color: Bs.secondary, light: Bs.secondaryLight },
  purple: { color: Bs.purple, light: Bs.purpleLight },
  dark: { color: Bs.dark, light: Bs.darkLight },
};

// Brands whose hue sits in the warm/green range, where the orange companion
// would blend in (or pair badly for red-green colorblindness) — these get the
// brand water blue as their comparison color instead.
const WARM_BRANDS = new Set(['danger', 'warning', 'success', 'info']);

// meterBrand resolves a meter's color name to its full brand palette,
// deriving the dark-surface base and a legible on-dark tint from the single
// brand hue. Unknown names fall back to water (the platform default).
export function meterBrand(colorName: string): MeterBrand {
  const b = METER_BRAND_BASE[colorName] ?? METER_BRAND_BASE.water;
  const compare = WARM_BRANDS.has(colorName) ? Bs.water : Bs.orange;
  return {
    solid: b.color,
    soft: b.light,
    onDark: mix(b.color, Bs.white, 0.4),
    base: mix(b.color, Bs.black, 0.8),
    compare,
    compareOnDark: mix(compare, Bs.white, 0.4),
    rgbString: rgbString(b.color),
  };
}

// App accent (buttons, active states, links, spinners) per theme mode.
// Dark mode uses the brand water blue; light mode uses the deeper brand
// "main" blue for stronger contrast against light surfaces.
export const BRAND_ACCENT_DARK = Bs.water;
export const BRAND_ACCENT_LIGHT = Bs.main;

// The "consumption yellow" the mobile app uses for the active period segment,
// selected chips and primary CTAs — with the ink color that stays legible on it.
export const CONSUMPTION_YELLOW = '#F5C518';
export const CONSUMPTION_YELLOW_INK = '#0A1A28';
